import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/Authcontext";
import { useLiveCounts } from "../utils/liveCounts";
import { formatINR } from "../utils/money";
import "../../src/assets/AdminCss/dropdown.css";

/* =====================================================================
   Side drawer
   =====================================================================
   The old menu was a flat list that mixed levels of the hierarchy:

     Home, Cricket, Football, Tennis, BlackJack, Poker, Indian Casinos,
     Sports, All Casino, Crash Games

   -- a section (Sports) sat below three of the sports inside it, and
   individual casino games sat beside casino categories. There was no way
   to tell what was a destination and what was a filter.

   It is now four groups with headings, so the shape of the product is
   legible from the menu alone, plus responsible-gambling and legal links
   at the foot where a gambling product should carry them.

   Icons are one family (Font Awesome, one size, one colour) rather than
   the previous mix of photographic casino art, flat PNGs and emoji-like
   marks at four different sizes.
   ===================================================================== */

const casino = (list) => ({ path: "/casinoGames", state: { list } });

/* Mirrors the top nav exactly. The menu and the nav disagreeing about
   what counts as Casino is how a visitor ends up not trusting either. */
const GROUPS = [
  {
    label: "Main",
    items: [
      { label: "Home", icon: "fa-home", path: "/" },
      { label: "Sports", icon: "fa-trophy", path: "/sportall" },
      { label: "Live", icon: "fa-tower-broadcast", path: "/inplay" },
    ],
  },
  {
    label: "Sports",
    items: [
      { label: "Cricket", icon: "fa-baseball-ball", path: "/cricketreal", countKey: "cricket" },
      { label: "Football", icon: "fa-futbol", path: "/footballreal", countKey: "football" },
      { label: "Tennis", icon: "fa-table-tennis", path: "/tennisreal", countKey: "tennis" },
      { label: "Other Sports", icon: "fa-medal", path: "/sportall" },
    ],
  },
  {
    // A dealer, a table, cards or a wheel.
    label: "Casino",
    items: [
      { label: "All Casino", icon: "fa-dice", ...casino(0) },
      { label: "Live Casino", icon: "fa-video", ...casino(7) },
      { label: "Roulette", icon: "fa-circle-notch", ...casino(9) },
      { label: "Baccarat", icon: "fa-clone", ...casino(10) },
      { label: "Blackjack", icon: "fa-layer-group", ...casino(15) },
      { label: "Teen Patti", icon: "fa-star", ...casino(4) },
      { label: "Poker", icon: "fa-diamond", ...casino(14) },
      { label: "Table Games", icon: "fa-chess-board", ...casino(18) },
      { label: "Indian Games", icon: "fa-hand-sparkles", ...casino(5) },
    ],
  },
  {
    // Fast, solo, no dealer.
    label: "Games",
    items: [
      { label: "All Games", icon: "fa-gamepad", ...casino(21) },
      { label: "Crash Games", icon: "fa-plane", ...casino(11) },
      { label: "Slots", icon: "fa-coins", ...casino(16) },
      { label: "Jili Games", icon: "fa-bolt", ...casino(6) },
      { label: "Matka", icon: "fa-dice-d6", path: "/matka" },
    ],
  },
];

// Only meaningful once there is an account behind them, so this group is
// shown for signed-in users rather than showing dead links to everyone.
// It sits at the top, under the player's name: appended after ~25 sports
// and casino entries it was effectively hidden on a phone.
const ACCOUNT_GROUP = {
  label: "Account",
  items: [
    { label: "My Bets", icon: "fa-receipt", path: "/sports_bets", state: { activeTab: 0 } },
    { label: "Transactions", icon: "fa-list", path: "/sports_bets", state: { activeTab: 3 } },
    { label: "Profit & Loss", icon: "fa-chart-line", path: "/sports_bets", state: { activeTab: 4 } },
    { label: "Change Password", icon: "fa-user-shield", path: "/changepassword" },
    { label: "Support", icon: "fa-headphones", path: "/contact-us" },
  ],
};

const DESKTOP_MIN = 992;

export const Sidebar = ({ isSidebar, setSidebar, isDesktop: isDesktopProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userBalnc, logout } = useContext(AuthContext);

  // Most callers never passed isDesktop, so on a laptop they got the phone
  // behaviour: a dimming scrim over the page and a drawer that closed on
  // every click. The width is measured here and the prop is an override,
  // so every page behaves the same without touching fifteen call sites.
  const panelRef = useRef(null);

  const [isWide, setIsWide] = useState(
    typeof window !== "undefined" && window.innerWidth >= DESKTOP_MIN
  );

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= DESKTOP_MIN);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isDesktop = isDesktopProp === undefined ? isWide : isDesktopProp;

  // Published by the header, which is mounted alongside this on every
  // page that has a menu -- so the counts cost no extra requests.
  const liveCounts = useLiveCounts();

  const close = () => setSidebar(false);

  // An open drawer that scrolls the page behind it is the standard
  // mobile-drawer bug. Only locked on small screens, where the drawer
  // overlays the content rather than sitting beside it.
  useEffect(() => {
    if (!isSidebar || isDesktop) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isSidebar, isDesktop]);

  // Dismiss on Escape, and on any click outside the panel -- at every
  // width. Only the mobile overlay had a scrim before, so on a laptop the
  // menu stayed open until you found the control again.
  useEffect(() => {
    if (!isSidebar) return undefined;

    const onEsc = (e) => {
      if (e.key === "Escape") close();
    };

    const onDown = (e) => {
      const panel = panelRef.current;
      if (panel && panel.contains(e.target)) return;
      // The control that opens the menu must not count as "outside", or
      // its own click would close and immediately reopen the panel.
      if (e.target.closest && e.target.closest("[data-rb-menu-trigger]")) return;
      close();
    };

    document.addEventListener("keydown", onEsc);
    // Pointer, not mouse: this has to work for a tap as well.
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.removeEventListener("pointerdown", onDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebar]);

  if (!isSidebar) return null;

  const go = (item) => {
    navigate(item.path, item.state ? { state: item.state } : undefined);
    if (!isDesktop) close();
  };

  const isActive = (item) => {
    if (item.path !== location.pathname) return false;
    // Every casino category shares one route, so the route alone cannot
    // say which is active -- the category index has to match too.
    if (item.state && location.state) {
      if ("activeTab" in item.state) return location.state.activeTab === item.state.activeTab;
      return location.state.list === item.state.list;
    }
    return !item.state;
  };

  const groups = isAuthenticated ? [ACCOUNT_GROUP].concat(GROUPS) : GROUPS;

  // The header's user-ID chip is gone; the name lives here instead. The
  // header writes it to localStorage once GetUserInfo answers, and the
  // drawer is only rendered while open, so this read is current.
  let userName = "";
  try {
    userName = localStorage.getItem("username") || "";
  } catch (e) {
    userName = "";
  }
  const balance = Math.max(0, Number(userBalnc?.userTotalBal) || 0);

  return (
    <>
      {/* Spacer that holds a column open beside the content on desktop.
          It matches #sidebar's width exactly; when it was narrower the
          panel sat on top of the first odds column. */}
      <div
        id="sidebar-landing"
        className="sidebar"
        style={{
          width: isDesktop ? "288px" : "0px",
          minWidth: isDesktop ? "288px" : "0px",
          maxWidth: isDesktop ? "288px" : "0px",
          flexShrink: 0,
          overflow: "hidden",
        }}
      />

      {/* Scrim: on mobile the drawer is an overlay, and tapping outside it
          is how people expect to dismiss one. */}
      {!isDesktop && (
        <div
          onClick={close}
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(7, 21, 37, 0.6)",
            zIndex: 999,
          }}
        />
      )}

      <nav
        id="sidebar"
        ref={panelRef}
        className="rb-side collapse show"
        aria-label="Site menu"
        style={{ position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 1000 }}
      >
        <div className="rb-side__head">
          <h2 className="rb-side__title">Menu</h2>
          <button
            type="button"
            className="rb-side__close"
            onClick={close}
            aria-label="Close menu"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>

        {isAuthenticated && (
          <div className="rb-side__profile">
            <div className="rb-side__who">
              <span className="rb-side__avatar" aria-hidden="true">
                <i className="fa fa-user" />
              </span>
              <span className="rb-side__whotext">
                <span className="rb-side__username">{userName || "My account"}</span>
                <span className="rb-side__balance">Balance {formatINR(balance)}</span>
              </span>
            </div>
            <div className="rb-side__money">
              <button
                type="button"
                className="rb-btn rb-btn--primary rb-btn--sm"
                onClick={() => go({ path: "/deposit" })}
              >
                Deposit
              </button>
              <button
                type="button"
                className="rb-btn rb-btn--secondary rb-btn--sm"
                onClick={() => go({ path: "/withdraw" })}
              >
                Withdraw
              </button>
            </div>
          </div>
        )}

        <div className="rb-side__body">
          {groups.map((group) => (
            <div className="rb-side__group" key={group.label}>
              <div className="rb-side__label">{group.label}</div>
              {group.items.map((item) => (
                <button
                  type="button"
                  key={group.label + "-" + item.label}
                  className={
                    isActive(item) ? "rb-side__item rb-side__item--active" : "rb-side__item"
                  }
                  onClick={() => go(item)}
                >
                  <span className="rb-side__icon">
                    <i className={"fa " + item.icon} aria-hidden="true" />
                  </span>
                  {item.label}
                  {item.countKey && liveCounts[item.countKey] ? (
                    <span
                      className="rb-side__count"
                      title={liveCounts[item.countKey] + " live now"}
                    >
                      {liveCounts[item.countKey]}
                    </span>
                  ) : null}
                </button>
              ))}
              {isAuthenticated && group === ACCOUNT_GROUP && (
                <button
                  type="button"
                  className="rb-side__item rb-side__item--logout"
                  onClick={() => {
                    close();
                    logout();
                  }}
                >
                  <span className="rb-side__icon">
                    <i className="fa fa-right-from-bracket" aria-hidden="true" />
                  </span>
                  Logout
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Responsible gambling is part of the product, not fine print. */}
        <a
          className="rb-side__rg"
          href="/responsible-gaming"
          onClick={(e) => {
            e.preventDefault();
            go({ path: "/responsible-gaming" });
          }}
        >
          <span className="rb-side__icon" aria-hidden="true">
            <i className="fa fa-shield-halved" />
          </span>
          <span>
            <strong>Responsible gambling</strong>
            Set limits, take a break, or get help.
          </span>
        </a>

        <div className="rb-side__foot">
          <a
            href="/rules"
            onClick={(e) => {
              e.preventDefault();
              go({ path: "/rules" });
            }}
          >
            Terms
          </a>
          <a
            href="/cookie-policy"
            onClick={(e) => {
              e.preventDefault();
              go({ path: "/cookie-policy" });
            }}
          >
            Privacy
          </a>
          <a
            href="/contact-us"
            onClick={(e) => {
              e.preventDefault();
              go({ path: "/contact-us" });
            }}
          >
            Help
          </a>
          <span style={{ color: "var(--rb-text-3)", fontSize: "12px" }}>18+</span>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
